import {
	ButtonWithLoading,
	ErrorModal,
	HelmetDexteel,
	MesfModal,
} from "@dexteel/mesf-core";
import {
	Button,
	FormControl,
	Grid2 as Grid,
	InputLabel,
	MenuItem,
	Select,
} from "@mui/material";
import { get } from "lodash-es";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useModalStyles } from "../../../../eaf-processes/styles/modalStyles";
import { useEAFDataEntryActions } from "../../../hooks/useEAFDataEntryActions";
import { ElectrodeEvent, Probe } from "../../../models/eaf.models";
import { PhaseEventSelector } from "../../PhaseEventSelector";
import { DateTimePickerDE } from "../../form-fields/common/DateTimePickerDE";

type Props = {
	show: boolean;
	onHide: () => void;
	date: Date;
	limitDates?: {
		start: Date;
		end: Date | null;
	};
	electrodeEvent?: ElectrodeEvent;
};
export const UpsertElectrodeEvent = ({
	show,
	onHide,
	date,
	limitDates,
	electrodeEvent,
}: Props) => {
	const { saveElectrodeEvent } = useEAFDataEntryActions();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [open, setOpen] = useState(false);
	const classes = useModalStyles();
	const {
		getValues,
		handleSubmit,
		setValue,
		control,
		reset,
		formState: { errors },
	} = useForm<ElectrodeEvent>({});

	const onSubmit: SubmitHandler<ElectrodeEvent> = async (
		data: ElectrodeEvent,
	) => {
		setIsSubmitLoading(true);
		try {
			await saveElectrodeEvent(data);
			setOpen(true);
			onHide();
		} catch (error) {
			setError(get(error, "message", "An unexpected error has occurred"));
		}
		setIsSubmitLoading(false);
	};

	useEffect(() => {
		if (show) {
			if (electrodeEvent) {
				reset(electrodeEvent);
			} else {
				reset();
				setValue("Timestamp", date);
				setValue("PhaseEventTypeId", 0);
				setValue("Phase", "A");
			}
		}
	}, [show, electrodeEvent]);
	return (
		<>
			{show && (
				<HelmetDexteel
					title={`${electrodeEvent ? "Edit" : "New"} Manual Electrode Event`}
				/>
			)}
			<Grid container>
				<Grid>
					<MesfModal
						title={
							electrodeEvent ? "EDIT ELECTRODE EVENT" : "NEW ELECTRODE EVENT"
						}
						open={show}
						handleClose={() => onHide()}
						id="upsert-electrode-event-modal"
						maxWidth="sm"
					>
						<form onSubmit={handleSubmit(onSubmit)}>
							<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
								<Grid container spacing={2}>
									<Grid size={{ xs: 12, md: 6 }}>
										<Controller
											name="Phase"
											control={control}
											rules={{
												required: "Phase is required",
											}}
											render={({ field }) => (
												<FormControl variant="outlined" fullWidth>
													<InputLabel id="phaseSelector">Phase</InputLabel>
													<Select
														fullWidth
														labelId="phaseSelector"
														id="phaseSelector"
														value={field.value}
														onChange={field.onChange}
														label="Phase"
													>
														<MenuItem value="A">A</MenuItem>
														<MenuItem value="B">B</MenuItem>
														<MenuItem value="C">C</MenuItem>
													</Select>
												</FormControl>
											)}
										/>
										{errors.Phase && (
											<span className={classes.errorLabel}>
												{errors.Phase.message}
											</span>
										)}
									</Grid>

									<Grid size={{ xs: 12, md: 6 }} style={{ marginTop: "4px" }}>
										<Controller
											name="Timestamp"
											control={control}
											defaultValue={new Date()}
											rules={{
												required: "Timestamp is required",
												validate: (value) => {
													if (limitDates?.start && value < limitDates.start) {
														return "Timestamp must be between the start and end of the process";
													} else if (
														limitDates?.end &&
														value > limitDates.end
													) {
														return "Timestamp must be less than the end of the process";
													} else {
														return true;
													}
												},
											}}
											render={({ field }) => (
												<DateTimePickerDE
													{...field}
													format={"HH:mm - MM/DD/YYYY"}
													value={getValues("Timestamp")}
													label="Time"
													error={errors.Timestamp?.message}
													onChange={(val) => {
														if (val) {
															setValue("Timestamp", val);
														}
													}}
													onBlur={() => {}}
												/>
											)}
										/>
									</Grid>
									<Grid size={{ xs: 12, md: 12 }}>
										<Controller
											name="PhaseEventTypeId"
											control={control}
											rules={{
												required: "Phase Event Type is required",
												validate: (value) => {
													if (value === 0) {
														return "Phase Event Type is required";
													}
													return true;
												},
											}}
											render={({ field }) => (
												<PhaseEventSelector
													value={field.value}
													onChange={field.onChange}
													disabled={false}
													error={errors.PhaseEventTypeId?.message}
													helperText={errors.PhaseEventTypeId?.message}
												/>
											)}
										/>
									</Grid>
								</Grid>
							</MesfModal.Content>
							<MesfModal.Actions style={{ padding: "20px" }}>
								<Grid container spacing={2} justifyContent="flex-end">
									<Grid size={{ xs: 12, md: 6 }} style={{ margin: 0 }}>
										<Button
											fullWidth
											variant="contained"
											color="inherit"
											onClick={() => onHide()}
											size="large"
										>
											Cancel
										</Button>
									</Grid>
									<Grid size={{ xs: 12, md: 6 }} style={{ margin: 0 }}>
										<ButtonWithLoading
											fullWidth
											isLoading={isSubmitLoading}
											variant="contained"
											color="primary"
											type="submit"
											size="large"
										>
											Save
										</ButtonWithLoading>
									</Grid>
								</Grid>
							</MesfModal.Actions>
						</form>
					</MesfModal>
				</Grid>
			</Grid>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title={`Error ${
					electrodeEvent ? "Updating" : "Creating"
				} Electrode Event`}
			/>
		</>
	);
};
